import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from "@angular/common/http";
import { inject } from "@angular/core";
import { AuthService } from "@partikelart/portal/data-access-auth";
import { OAuthService } from "angular-oauth2-oidc";
import { Observable, race, timer } from "rxjs";
import { filter, switchMap, take } from "rxjs/operators";
import { ENV_CONFIG } from "../app.token";

/**
 * Requests that are part of the login bootstrap itself. They must never wait
 * for a token, because they are what *produces* one:
 *
 * - `/api/auth/login-config` is public (home-realm discovery, D3) and is
 *   fetched by the auth APP_INITIALIZER before `OAuthService.configure()` has
 *   ever run. Gating it on a token is a deadlock that can only resolve by
 *   timing out — it used to cost every cold load a fixed 5 s.
 * - `/api/auth/token` and `/api/auth/userinfo` are the `token-proxy` back
 *   channel; angular-oauth2-oidc issues them and sets its own headers.
 */
const AUTH_BOOTSTRAP_PATHS = ["/api/auth/login-config", "/api/auth/token", "/api/auth/userinfo"];

/**
 * Safety net for an initial login sequence that never settles at all.
 * `AuthService.runInitialLoginSequence` flips `isDoneLoading$` on both success
 * and failure, so reaching this deadline means auth is broken; the request then
 * goes out unauthenticated (and is rejected by the backend) rather than hanging
 * forever. It is never part of a healthy load.
 */
const LOGIN_SEQUENCE_DEADLINE_MS = 15_000;

/**
 * Functional HTTP interceptor for the portal-backend.
 *
 * Only requests whose URL starts with `portalBackendUrl` are affected.
 * Authentication strategy, applied in order:
 *
 * 1. **Login bootstrap** — passed through untouched (see `AUTH_BOOTSTRAP_PATHS`).
 * 2. **Static API key** — if `portalApiKey` is configured it is attached as
 *    `x-api-key: <key>` (machine identity; no user login involved).
 * 3. **Demo builds** — no identity provider exists at all, nothing to wait for.
 * 4. **OAuth bearer token** — the request waits for the initial login sequence
 *    to settle, then attaches `Authorization: Bearer <token>` if one is
 *    available.
 */
export const portalApiInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const config = inject(ENV_CONFIG);
	const portalBackendUrl = config.portalBackendUrl;

	if (!portalBackendUrl || !req.url.startsWith(portalBackendUrl)) {
		return next(req);
	}

	const path = req.url.slice(portalBackendUrl.length);
	if (AUTH_BOOTSTRAP_PATHS.some((bootstrapPath) => path === bootstrapPath || path.startsWith(`${bootstrapPath}?`))) {
		return next(req);
	}

	if (config.portalApiKey) {
		return next(req.clone({ setHeaders: { "x-api-key": config.portalApiKey } }));
	}

	if (config.demo) {
		return next(req);
	}

	const oauthService = inject(OAuthService);
	const authService = inject(AuthService);

	// `isDoneLoading$` is BehaviorSubject-backed: once the login sequence has
	// settled this emits synchronously and the request goes out with no delay.
	return race(
		authService.isDoneLoading$.pipe(filter((isDone): isDone is true => isDone)),
		timer(LOGIN_SEQUENCE_DEADLINE_MS),
	).pipe(
		take(1),
		switchMap(() => {
			const token = oauthService.getAccessToken() || oauthService.getIdToken();
			if (!token) {
				return next(req);
			}
			return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
		}),
	);
};
